import type { IsEmpty, IsNonEmpty, IsNullable } from './types';

export function isEmpty<T>(value: T): value is IsEmpty<T> {
  return isNullish(value) || value === '';
}

export function isNonEmpty<T>(value: T): value is IsNonEmpty<T> {
  return !isEmpty(value);
}

export function isNullish<T>(x: T): x is IsNullable<T> {
  return x == null;
}

export function isNonNullish<T>(x: T): x is NonNullable<T> {
  return x != null;
}

export function isValidDate(x: unknown): x is Date {
  return isDateObject(x) && !Number.isNaN(+x);
}

export function isNumber(x: unknown): x is number {
  return typeof x === 'number';
}

export type NumberOrNull<T> = T extends number | null ? T : never;

export function isNumberOrNull<T>(value: T): value is NumberOrNull<T> {
  return value == null || !Number.isNaN(value);
}

export function isDateObject(x: unknown): x is Date {
  return x instanceof Date;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isFunction<T extends (...args: any[]) => any>(x: unknown): x is T {
  return typeof x === 'function';
}

export const isRegex = (value: unknown): value is RegExp => value instanceof RegExp;

export const isString = (value: unknown): value is string => typeof value === 'string';

export const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean';

// eslint-disable-next-line @typescript-eslint/ban-types
export const isObjectType = (value: unknown): value is object => typeof value === 'object';

// eslint-disable-next-line @typescript-eslint/ban-types
export function isObject<T extends object>(value: unknown): value is T {
  return !isNullish(value) && !Array.isArray(value) && isObjectType(value) && !isDateObject(value);
}
